import React, { useEffect, useState } from "react"
import styled, { keyframes } from "styled-components"
import Img, { FluidObject } from "gatsby-image"

const ImageConveyorWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`

const ConveyorAnimation = keyframes`
  from {
    transform: translate3d(0%, 0%, 0.01px);
  }
  to {
    transform: translate3d(-50%, 0%, 0.01px);
  }
`

const ImageConveyorContainer = styled.div<{ width: number }>`
  display: flex;
  align-items: center;
  width: ${(props): number => props.width}px;
  animation: ${ConveyorAnimation} 40s infinite linear;
`

type ImageProps = {
  desktopWidth: number
  mobileWidth: number
  marginTop: number
}

const DESKTOP_SCREEN_SIZE = 992
const MARGIN_MOBILE = 20
const MARGIN_DESKTOP = 40

const Image = styled(Img)<ImageProps>`
  width: ${(props): number => props.mobileWidth}%;
  margin-top: ${(props): number => props.marginTop}%;
  margin-right: ${MARGIN_MOBILE}px;

  @media (min-width: ${DESKTOP_SCREEN_SIZE}px) {
    width: ${(props): number => props.desktopWidth}%;
    margin-right: ${MARGIN_DESKTOP}px;
  }
`

export type ImageSettings = {
  // Size in percentage
  mobileWidth: number
  // Size in percentage
  desktopWidth: number
  // Size in percentage
  marginTop: number
  image: FluidObject
}

type Props = {
  images: Array<ImageSettings>
}

const ImageConveyor: React.FC<Props> = ({ images, ...props }: Props) => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    let timeout: number | null = null
    const calculateWidth = (): void => {
      const windowWidth = window.innerWidth
      let width = 0

      for (const image of images) {
        if (windowWidth >= DESKTOP_SCREEN_SIZE) {
          width += (image.desktopWidth / 100) * windowWidth * 2
        } else {
          width += (image.mobileWidth / 100) * windowWidth * 2
        }
      }

      setWidth(width)
    }

    const handleWindowResize = () => {
      if (timeout) {
        window.clearTimeout(timeout)
      }

      timeout = window.setTimeout(() => {
        calculateWidth()
      }, 500)
    }

    calculateWidth()
    window.addEventListener("resize", handleWindowResize)

    return (): void => {
      window.removeEventListener("resize", handleWindowResize)
    }
  }, [images])

  return (
    <ImageConveyorWrapper {...props}>
      <ImageConveyorContainer width={width}>
        {images.map((image, index) => (
          <Image
            key={`original-image-${index}`}
            fluid={image.image}
            marginTop={image.marginTop}
            desktopWidth={image.desktopWidth / 2}
            mobileWidth={image.mobileWidth / 2}
          />
        ))}
        {images.map((image, index) => (
          <Image
            key={`copy-image-${index}`}
            fluid={image.image}
            marginTop={image.marginTop}
            desktopWidth={image.desktopWidth / 2}
            mobileWidth={image.mobileWidth / 2}
          />
        ))}
      </ImageConveyorContainer>
    </ImageConveyorWrapper>
  )
}

export default ImageConveyor
